import { get } from 'lodash';
import { RootState } from 'src/redux/store';
import { translationsRootKey, isDayforce } from '../index';
import { getFormattedDate } from '../../helpers/dateTime';
import { t, translateOptionLabel } from '../../i18n';

const getName = (data: { name: string }) => {
  return data && data.name ? data.name : '...';
};

const getUserName = (data: { first_name: string; last_name: string }) => {
  return data && data.first_name && data.last_name ? `${data.first_name} ${data.last_name}` : '...';
};

// Object form: `label` is the displayed text; `path` overrides the crumb's default
// link target (which is otherwise the matched URL prefix itself).
type BreadcrumbValue = string | { label: string; path: string } | ((state: RootState) => string);

const breadcrumbMap: () => { [key: string]: BreadcrumbValue } = () => {
  const map: { [key: string]: BreadcrumbValue } = {
    // Root breadcrumbs
    '/': `${t('dash.breadcrumbs.jobs')} - ${t('dash.breadcrumbs.active_job')}`,
    '/?tab=past': `${t('dash.breadcrumbs.jobs')} - ${t('dash.breadcrumbs.past_job')}`,
    '/?tab=active': `${t('dash.breadcrumbs.jobs')} - ${t('dash.breadcrumbs.active_job')}`,
    '/?tab=future': `${t('dash.breadcrumbs.jobs')} - ${t('dash.breadcrumbs.future_job')}`,

    'dashboard/agencies': `${t('dash.dashboard.dashboard_title')} - ${t('dash.breadcrumbs.agencies')}`,
    'dashboard/demand': `${t('dash.dashboard.dashboard_title')} - ${t('dash.breadcrumbs.demand')}`,
    'dashboard/supply': `${t('dash.dashboard.dashboard_title')} - ${t('dash.breadcrumbs.supply')}`,
    'dashboard/applicant': `${t('dash.dashboard.dashboard_title')} - ${t('dash.breadcrumbs.applicant')}`,

    // Leads breadcrumbs
    leads: t('dash.breadcrumbs.leads'),

    // Companies breadcrumbs
    companies: t(`${translationsRootKey}.breadcrumbs.companies`),
    'companies/:id': (state: RootState) => {
      const data = state.Company.company;

      if (data && !data.id) {
        return t(`${translationsRootKey}.breadcrumbs.add_company`);
      }

      return getName(data);
    },
    'companies/:id/edit': t(`${translationsRootKey}.breadcrumbs.edit_company`),
    'companies/:id/rules': t('dash.breadcrumbs.contact_specific_agreements'),
    'companies/:id/editConversionRule': t('dash.breadcrumbs.talent_conversion_rules'),

    // Division breadcrumbs
    divisions: t(`${translationsRootKey}.breadcrumbs.divisions`),
    'divisions/:id': (state: RootState) => {
      const data = state.Division.division;

      if (data && !data.id) {
        return t(`${translationsRootKey}.breadcrumbs.add_division`);
      }

      return getName(data);
    },
    'divisions/:id/edit': t(`${translationsRootKey}.breadcrumbs.edit_division`),

    // Units breadcrumbs
    units: t('dash.breadcrumbs.units'),
    'units/:id': (state: RootState) => {
      const data = state.Client.client;

      if (data && !data.id) {
        return t('dash.breadcrumbs.add_unit');
      }

      return getName(data);
    },
    'units/:id/price-lists/add': t('dash.breadcrumbs.unit_add_pricing'),
    'units/:id/edit': t('dash.breadcrumbs.edit_unit'),
    'units/:id/invoices/:invoiceId': t('dash.breadcrumbs.invoice'),
    'units/:id/managers/:managerId': (state: RootState) => {
      const data = state.Manager.manager;

      if (data && !data.id) {
        return t('dash.breadcrumbs.add_manager');
      }

      return getUserName(data);
    },
    'units/:id/managers/:managerId/edit': t('dash.breadcrumbs.edit_manager'),
    'units/:id/cards/:cardId': (state: RootState) => {
      const { card } = state.Card;

      if (state.Card.isEdit) {
        return card.last4 ? t('dash.breadcrumbs.edit_credit') : t('dash.breadcrumbs.add_credit');
      }

      return t('dash.breadcrumbs.card_details');
    },
    'units/:id/bankAccounts/:bankAccountId': (state: RootState) =>
      state.BankAccount.isAdd ? t('dash.breadcrumbs.add_bank_account') : t('dash.breadcrumbs.bank_account'),
    'units/:id/credits/:creditId': (state: RootState) => {
      const data = state.Credit.credit;

      if (data && !data.creator) {
        return t('dash.breadcrumbs.add_credit');
      }

      if (data && state.Credit.isEdit) {
        return t('dash.breadcrumbs.edit_credit');
      }

      return getName(data);
    },
    'units/:id/editAgencies': t('dash.breadcrumbs.edit_unit_agencies'),

    // SubUnits breadcrumbs
    subUnits: t('dash.breadcrumbs.subunits'),
    'subUnits/:id': (state: RootState) => {
      const data = state.SubUnit.subUnit;

      if (data && !data.id) {
        return t('dash.breadcrumbs.add_subunit');
      }

      return getName(data);
    },
    'subUnits/:id/edit': t('dash.breadcrumbs.edit_subunit'),
    'subUnits/:id/invoices/:invoiceId': t('dash.breadcrumbs.invoice'),
    'subUnits/:id/managers/:managerId': (state: RootState) => {
      const data = state.SubUnitManager.manager;

      if (data && !data.id) {
        return t('dash.breadcrumbs.add_manager');
      }

      return getUserName(data);
    },
    'subUnits/:id/price-lists/add': t('dash.breadcrumbs.unit_add_pricing'),
    'subUnits/:id/managers/:managerId/edit': t('dash.breadcrumbs.edit_manager'),
    'subUnits/:id/cards/:cardId': (state: RootState) => {
      const { card } = state.SubUnitCard;

      if (state.SubUnitCard.isEdit) {
        return card.last4 ? t('dash.breadcrumbs.edit_credit') : t('dash.breadcrumbs.add_credit');
      }

      return t('dash.breadcrumbs.card_details');
    },
    'subUnits/:id/credits/:creditId': (state: RootState) => {
      const data = state.SubUnitCredit.credit;

      if (data && !data.creator) {
        return t('dash.breadcrumbs.add_credit');
      }

      if (data && state.Credit.isEdit) {
        return t('dash.breadcrumbs.edit_credit');
      }

      return getName(data);
    },
    'subUnits/:id/bankAccounts/:bankAccountId': (state: RootState) =>
      state.SubUnitBankAccount.isAdd ? t('dash.breadcrumbs.add_bank_account') : t('dash.breadcrumbs.bank_account'),

    agencies: t('dash.breadcrumbs.agencies'),
    'agencies/active': (state: RootState) =>
      state.StartAppSettings?.feature_flags?.legal_entity_enabled
        ? t('dash.breadcrumbs.agencies_active_contracts')
        : t('dash.breadcrumbs.agencies_active'),
    'agencies/invite': (state: RootState) =>
      state.StartAppSettings?.feature_flags?.legal_entity_enabled
        ? t('dash.breadcrumbs.agencies_invite_contracts')
        : t('dash.breadcrumbs.agencies_invite'),
    'agencies/removed': t('dash.breadcrumbs.agencies_deactivated'),
    'agencies/invoices': t('dash.breadcrumbs.agencies_invoices'),
    'agencies/invite/add': (state: RootState) =>
      state.StartAppSettings?.feature_flags?.legal_entity_enabled
        ? t('dash.breadcrumbs.agencies_add_contract')
        : t('dash.breadcrumbs.agencies_add'),
    'agencies/active/:id': (state: RootState) =>
      state.AgencyView?.agency?.name || state.AgencyCreateEdit?.originalAgencyName || '',
    'agencies/invite/:id': (state: RootState) =>
      state.AgencyView?.agency?.name || state.AgencyCreateEdit?.originalAgencyName || '',
    // 'agencies/contracts' is a route prefix, not a page: the contract page sets its crumb from the contract's status.
    'agencies/contracts/:id': (state: RootState) => state.AgencyContractsV2View?.contract?.agency?.name || '...',
    'agencies/invite/:id/edit?*': t('dash.breadcrumbs.agencies_edit'),
    'agencies/active/:id/edit': t('dash.agencies.update_contract.update_contract_header'),

    // Agency Workers breadcrumbs
    agencyWorkers: t('dash.breadcrumbs.workforce'),
    'agencyWorkers/:id': (state: RootState) => state.ViewAgencyWorker?.workerIdentity?.workers[0]?.full_name || '...',
    'agencyWorkers/:id/past': t('dash.breadcrumbs.past_shifts'),
    'agencyWorkers/:id/active': t('dash.breadcrumbs.active_shifts'),
    'agencyWorkers/:id/future': t('dash.breadcrumbs.future_shifts'),

    // Applicants breadcrumbs
    applicants: t('dash.breadcrumbs.applicants'),
    'applicants/:id': (state: RootState) => {
      const data = state.Applicant.applicant;

      if (data && !data.id) {
        return t('dash.breadcrumbs.add_applicant');
      }

      return getUserName(data);
    },
    'applicants/:id/edit': t('dash.breadcrumbs.edit_applicant'),
    'applicants/:id/editSuperannuation': t('dash.breadcrumbs.applicant_edit_superannuation'),

    // Denied Applicants breadcrumbs
    denied: t('dash.breadcrumbs.denied'),
    'denied/:id': (state: RootState) => getUserName(state.Applicant.applicant),
    'denied/:id/edit': t('dash.breadcrumbs.edit_denied'),

    // Interviews breadcrumbs
    interviews: t('dash.breadcrumbs.interviews'),
    'interviews/:id': (state: RootState) => {
      const data = state.Applicant.applicant;

      if (data && !data.id) {
        return t('dash.breadcrumbs.add_interview');
      }

      return getUserName(data);
    },
    'interviews/:id/edit': t('dash.breadcrumbs.edit_interview'),

    // Talents breadcrumbs
    talent: t(`${translationsRootKey}.breadcrumbs.talent`),
    'talent/:id': (state: RootState) => getUserName(state.Talent.talent),
    'talent/:id/edit': t(`${translationsRootKey}.breadcrumbs.edit_talent`),
    'talent/:id/talentAccreditations/:talentAccreditationId': t(
      `${translationsRootKey}.breadcrumbs.talent_accreditation`,
    ),
    'talent/:id/editSuperannuation': t('dash.breadcrumbs.talent_edit_superannuation'),
    'talent/:id/pastJobs': t('dash.breadcrumbs.talent_past_jobs'),

    // Terminated talents breadcrumbs
    terminated: t('dash.breadcrumbs.terminated'),
    'terminated/:id': (state: RootState) => getUserName(state.Talent.talent),

    // Jobs breadcrumbs
    attentionRequired: `${t('dash.breadcrumbs.jobs')} - ${t('dash.breadcrumbs.attention_required')}`,
    timesheets: `${t('dash.breadcrumbs.timesheets')}`,
    'jobs/:id': t('dash.breadcrumbs.job_details'),
    'jobs?*tab=past': `${t('dash.breadcrumbs.jobs')} - ${t('dash.breadcrumbs.past_job')}`,
    'jobs?*tab=active': `${t('dash.breadcrumbs.jobs')} - ${t('dash.breadcrumbs.active_job')}`,
    'jobs?*tab=future': `${t('dash.breadcrumbs.jobs')} - ${t('dash.breadcrumbs.future_job')}`,
    'jobs?*tab=calendar_view': `${t('dash.breadcrumbs.jobs')} - ${t('dash.breadcrumbs.calendar')}`,
    'jobs?*tab=past&company_id=*': `${t('dash.breadcrumbs.jobs')} - ${t('dash.breadcrumbs.past_job')}`,
    'jobs?*tab=active&company_id=*': `${t('dash.breadcrumbs.jobs')} - ${t('dash.breadcrumbs.active_job')}`,
    'jobs?*tab=future&company_id=*': `${t('dash.breadcrumbs.jobs')} - ${t('dash.breadcrumbs.future_job')}`,
    'jobs?*tab=past&worker_id=*': `${t('dash.breadcrumbs.jobs')} - ${t('dash.breadcrumbs.past_job')}`,
    'jobs?*tab=active&worker_id=*': `${t('dash.breadcrumbs.jobs')} - ${t('dash.breadcrumbs.active_job')}`,
    'jobs?*tab=future&worker_id=*': `${t('dash.breadcrumbs.jobs')} - ${t('dash.breadcrumbs.future_job')}`,
    'jobs/multiDay': t('dash.breadcrumbs.multi_job'),
    'jobs/multiDay?*tab=past&job_group_id=*': `${t('dash.breadcrumbs.jobs')} - ${t('dash.breadcrumbs.past_job')}`,
    'jobs/multiDay?*tab=active&job_group_id=*': `${t('dash.breadcrumbs.jobs')} - ${t('dash.breadcrumbs.active_job')}`,
    'jobs/multiDay?*tab=future&job_group_id=*': `${t('dash.breadcrumbs.jobs')} - ${t('dash.breadcrumbs.future_job')}`,
    'jobs/multiDay?*tab=*&job_group_id=*': t('dash.breadcrumbs.multi_job'),

    'jobs/:id/addPromocode': t('dash.breadcrumbs.add_promo_code'),
    'jobs/multiDay/:id/addPromocode': t('dash.breadcrumbs.add_promo_code'),
    'jobs/:id/addGig': t('dash.breadcrumbs.add_gig'),
    'jobs/multiDay/:id/addGig': t('dash.breadcrumbs.add_gig'),
    'jobs/:id/bookings/:bookingId/pool': t('dash.breadcrumbs.pool'),
    'jobs/multiDay/:id/bookings/:bookingId/pool': t('dash.breadcrumbs.pool'),
    'jobs/:id/bookings/:bookingId/editBackups': t('dash.breadcrumbs.edit_backups'),
    'jobs/multiDay/:id/bookings/:bookingId/editBackups': t('dash.breadcrumbs.edit_backups'),
    'jobs/:id/bookings/:bookingId/editBooking': t('dash.breadcrumbs.edit_booking'),
    'jobs/multiDay/:id/bookings/:bookingId/editBooking': t('dash.breadcrumbs.edit_booking'),

    // Accreditations breadcrumbs
    accreditations: t('dash.breadcrumbs.accreditations'),
    'accreditations/:id': (state: RootState) => {
      const data = state.AccreditationsSettings.accreditation;

      if (data && !data.id) {
        return t('dash.breadcrumbs.add_accreditation');
      }

      if (state.AccreditationsSettings.isEdit) {
        return t('dash.breadcrumbs.edit_accreditation');
      }

      return getName(data);
    },
    // Clients Accreditations
    'units/:id/accreditations/add': t('dash.breadcrumbs.add_accreditation'),
    'subUnits/:id/accreditations/add': t('dash.breadcrumbs.add_accreditation'),
    'companies/:id/accreditations/add': t('dash.breadcrumbs.add_accreditation'),
    'divisions/:id/accreditations/add': t('dash.breadcrumbs.add_accreditation'),
    'units/:id/accreditations/:accreditationId/edit': t('dash.breadcrumbs.edit_accreditation'),
    'subUnits/:id/accreditations/:accreditationId/edit': t('dash.breadcrumbs.edit_accreditation'),
    'companies/:id/accreditations/:accreditationId/edit': t('dash.breadcrumbs.edit_accreditation'),
    'divisions/:id/accreditations/:accreditationId/edit': t('dash.breadcrumbs.edit_accreditation'),

    // Algorithm breadcrumbs
    algorithm: t('dash.breadcrumbs.algorithm'),

    // Attires breadcrumbs
    attires: t('dash.breadcrumbs.attires'),
    'attires?*tab=attires*': `${t('dash.breadcrumbs.attires')} - ${t('dash.breadcrumbs.attires')}`,
    'attires?*tab=shirts*': `${t('dash.breadcrumbs.attires')} - ${t('dash.breadcrumbs.shirts')}`,
    'attires?*tab=pants*': `${t('dash.breadcrumbs.attires')} -  ${t('dash.breadcrumbs.pants')}`,
    'attires?*tab=shoes*': `${t('dash.breadcrumbs.attires')} -  ${t('dash.breadcrumbs.shoes')}`,
    'attires/:id/edit': t('dash.breadcrumbs.edit_attire'),
    'attires/shirts/:id': t('dash.breadcrumbs.attire_details'),
    'attires/shoes/:id': t('dash.breadcrumbs.attire_details'),
    'attires/pants/:id': t('dash.breadcrumbs.attire_details'),

    // Automation Config breadcrumbs
    automationConfig: t('dash.breadcrumbs.automation_config'),

    // Cancellation Policy breadcrumbs
    cancellationPolicy: t('dash.breadcrumbs.cancellation_policy'),

    // Ondemand App Options breadcrumbs
    clientFeedbackOptions: t(`${translationsRootKey}.breadcrumbs.client_feedback_options`),

    // Organization breadcrumbs
    organization: t(`${translationsRootKey}.breadcrumbs.organization`),

    // Demo Users breadcrumbs
    demoUsers: t('dash.breadcrumbs.demo_users'),
    'demoUsers/:id': (state: RootState) => {
      const data = state.DemoUser.demoUser;

      if (data && !data.first_name) {
        return t('dash.breadcrumbs.add_demo_user');
      }

      return getUserName(data);
    },
    'demoUsers/:id/edit': t('dash.breadcrumbs.edit_demo_user'),

    // Districts breadcrumbs
    districts: t('dash.breadcrumbs.districts'),
    'districts/:id': (state: RootState) => {
      const data = state.District.district;

      if (data && !data.id) {
        return t('dash.breadcrumbs.add_district');
      }

      return getName(data);
    },
    'districts/:id/edit': t('dash.breadcrumbs.edit_district'),

    // Flex App Options breadcrumbs
    flexAppOptions: t(`${translationsRootKey}.breadcrumbs.flex_app_options`),

    // Guides breadcrumbs
    'user-guide': t('dash.breadcrumbs.user_guide'),

    // Holiday Pricing breadcrumbs
    holidayPricing: t('dash.breadcrumbs.holiday_pricing'),

    // 'pricing' is not a page, so it gets no crumb of its own: the trail starts at the list page
    // below, and a card or engine hangs under its own list.
    'pricing/rate-engine': t('dash.breadcrumbs.rate_engines'),
    'pricing/rate-engine/:id': (state: RootState) => state.ViewRateEngine?.engine?.name || '...',
    'pricing/rate-cards': t('dash.breadcrumbs.rate_cards'),
    'pricing/rate-cards/:id': (state: RootState) => state.RateCardView?.card?.name || '...',

    markets: t('dash.breadcrumbs.markets'),
    'markets/:id': (state: RootState) => {
      const data = state.Market.market;

      return getName(data);
    },

    // Partners breadcrumbs
    partners: t('dash.breadcrumbs.partners'),
    'partners/:id': (state: RootState) => {
      const data = state.Partner.partner;

      if (data && !data.id) {
        return t('dash.breadcrumbs.add_partner');
      }

      return getName(data);
    },
    'partners/:id/edit': t('dash.breadcrumbs.edit_partner'),

    // Payroll breadcrumbs
    payroll: t('dash.breadcrumbs.payroll'),
    'payroll/export': t('dash.breadcrumbs.export'),

    // Data Uploads breadcrumbs
    dataUploads: t('dash.breadcrumbs.data_uploads'),

    // Legal Entities breadcrumbs
    legalEntities: t('dash.breadcrumbs.legal_entities'),
    'legalEntities/create': t('dash.breadcrumbs.legal_entity_create'),
    'legalEntities/:id/edit': t('dash.breadcrumbs.legal_entity_edit'),

    // Positions breadcrumbs
    positions: t('dash.breadcrumbs.positions'),
    'positions/:id': (state: RootState) => {
      const data = state.Position.position;

      if (data && !data.id) {
        return t('dash.breadcrumbs.add_position');
      }

      if (data && data.id && state.Position.isEdit) {
        return t('dash.breadcrumbs.edit_position');
      }

      return getName(data);
    },

    // Promo Codes breadcrumbs
    promoCodes: t('dash.breadcrumbs.promo_codes'),
    'promoCodes/:id': (state: RootState) => {
      const data = state.PromoCode.promoCode;

      if (data && !data.code) {
        return t('dash.breadcrumbs.add_promo_code');
      }

      return data && data.code ? data.code : '...';
    },
    'promoCodes/:id/edit': t('dash.breadcrumbs.edit_promo_code'),

    // Referrals breadcrumbs
    referrals: t('dash.breadcrumbs.referrals'),

    // Sectors breadcrumbs
    sectors: t('dash.breadcrumbs.sectors'),
    'sectors/:id': (state: RootState) => {
      const data = state.Sector.sector;

      if (data && !data.id) {
        return t('dash.breadcrumbs.add_sector');
      }

      return getName(data);
    },
    'sectors/:id/edit': t('dash.breadcrumbs.edit_sector'),

    // Terms And Privacy breadcrumbs
    termsAndPrivacy: t('dash.breadcrumbs.terms_and_privacy'),

    // Users breadcrumbs
    users: t('dash.breadcrumbs.users'),
    'users/:id': (state: RootState) => {
      const data = state.User.user;

      if (data && !data.first_name) {
        return t('dash.breadcrumbs.add_user');
      }

      return data && data.first_name && data.last_name && data.role.name
        ? `${data.first_name} ${data.last_name} - ${translateOptionLabel('role', data.role.id)}`
        : '...';
    },
    'users/:id/edit': t('dash.breadcrumbs.edit_user'),

    // Profile breadcrumbs
    profile: t('dash.breadcrumbs.profile'),

    // Settings breadcrumbs
    settings: t('dash.breadcrumbs.settings'),

    // Notification Center breadcrumbs
    'notifications?*': t('dash.breadcrumbs.notification_center'),
    'notifications?*tab=active*': t('dash.breadcrumbs.active_notifications'),
    'notifications?*tab=resolved*': t('dash.breadcrumbs.resolved_notifications'),

    // Sessions breadcrumbs
    sessions: t('dash.breadcrumbs.sessions'),
    'sessions?*tab=past*': t('dash.breadcrumbs.sessions'),
    'sessions?*tab=active*': t('dash.breadcrumbs.sessions'),
    'sessions?*tab=future*': t('dash.breadcrumbs.sessions'),
    'sessions/add': t('dash.breadcrumbs.add_session'),
    'sessions/:clientName': (state: RootState) =>
      get(state, 'Session.orientation.client.name') || get(state, 'Session.session.client.name'),
    'sessions?:params/:id': (state: RootState) => {
      const names = [
        get(state, 'SessionListPast.sessions.orientation.name'),
        get(state, 'SessionListActive.sessions.orientation.name'),
        get(state, 'SessionListFuture.sessions.orientation.name'),
      ];

      return names.find(name => name);
    },
    'sessions/:clientName/session/:sessionId': (state: RootState) => {
      const sessionDate = get(state, 'Session.session.starts_at');

      return sessionDate ? getFormattedDate({ date: sessionDate, formatKey: 'date_day_month_year' }) : '';
    },
    'sessions/:orientation_id/settings': t('dash.breadcrumbs.settings'),

    // Some demo page breadcrumbs
    help: t('dash.breadcrumbs.help'),
  };

  if (isDayforce()) {
    map['industries/:id/addPosition'] = t('dash.breadcrumbs.add_position');

    // Organizations breadcrumbs
    map.enterprise_organizations = t('dash.breadcrumbs.organizations');
    map['enterprise_organizations/create'] = t('dash.breadcrumbs.organization_create');
    // map['enterprise_organizations/:id'] = state =>
    // state.Organization.isOrgCreation ? '' : getName(state.Organization.data);
    map['enterprise_organizations/:id/edit'] = t('dash.breadcrumbs.organization_edit');
  }

  return map;
};

export default breadcrumbMap;
